import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  CloseButton,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { SolidActionButton } from '@components/Buttons'
import { useApp } from '@hb/react'
import { LabResult, SaveLabResultArgs, WithId } from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { saveLabResult } from '../../../../backend/functions/gyn'
import { useGynCareView } from '../../../../components/Gyn/context'
import { LogLabResultForm } from '../../../../components/Users/Profile/Labs/LogLabResultForm'
import { useAssessmentLabResults } from '../../../../hooks/backend'
import { LabResultRow } from './LabResultRow'

type SortMode = 'pending-first' | 'date-desc' | 'date-asc' | 'lab-type'

const sortLabel: Record<SortMode, string> = {
  'pending-first': 'Pending First',
  'date-desc': 'Newest',
  'date-asc': 'Oldest',
  'lab-type': 'Lab Type',
}

const sortResults = (results: WithId<LabResult>[], mode: SortMode): WithId<LabResult>[] => {
  const copy = [...results]
  const dateOf = (r: LabResult) => r.resultDate ?? r.collectedDate ?? ''
  switch (mode) {
    case 'pending-first':
      return copy.sort((a, b) => {
        const aPending = a.flagStatus !== 'normal' && !a.acknowledgedAt ? 0 : 1
        const bPending = b.flagStatus !== 'normal' && !b.acknowledgedAt ? 0 : 1
        if (aPending !== bPending) return aPending - bPending
        return dateOf(b).localeCompare(dateOf(a))
      })
    case 'date-desc':
      return copy.sort((a, b) => dateOf(b).localeCompare(dateOf(a)))
    case 'date-asc':
      return copy.sort((a, b) => dateOf(a).localeCompare(dateOf(b)))
    case 'lab-type':
      return copy.sort((a, b) => a.labType.localeCompare(b.labType))
  }
}

interface Props {
  assessmentId: string
  patientId: string
  isOpen: boolean
  onClose: () => void
}

export const LabQuickview = ({ assessmentId, patientId, isOpen, onClose }: Props) => {
  const { appName } = useApp()
  const { practiceId } = useGynCareView()
  const { dataArr, loading } = useAssessmentLabResults(patientId, assessmentId)
  const [sortMode, setSortMode] = useState<SortMode>('pending-first')
  const [isLogging, setIsLogging] = useState(false)

  const results = useMemo(() => dataArr ?? [], [dataArr])
  const sorted = useMemo(() => sortResults(results, sortMode), [results, sortMode])

  const pendingCount = useMemo(
    () => results.filter(r => r.flagStatus !== 'normal' && !r.acknowledgedAt).length,
    [results],
  )

  const openLog = useCallback(() => setIsLogging(true), [])
  const closeLog = useCallback(() => setIsLogging(false), [])

  const handleSave = useCallback(
    async (args: Partial<SaveLabResultArgs> & { labResultId: string }) => {
      if (!practiceId) return
      await saveLabResult({
        assessmentId,
        patientId,
        practiceId,
        appName,
        labType: 'other',
        labName: '',
        result: '',
        flagStatus: 'normal',
        ...args,
      })
    },
    [assessmentId, patientId, practiceId, appName],
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius={8} h="85vh" display="flex" flexDir="column">
        <ModalHeader
          fontFamily="Hero-New"
          bg="gray.50"
          borderTopRadius={8}
          borderBottom="1px solid #cdcdcd"
          py={2}
          px={4}
          fontSize="md">
          <Flex align="center" gap={3} flexWrap="wrap">
            <Text fontFamily="Hero-New" fontWeight={500} fontSize="lg">
              {isLogging ? 'Log Lab Result' : 'Lab Results'}
            </Text>
            {!isLogging && pendingCount > 0 && (
              <Badge colorScheme="orange" fontFamily="Hero-New" fontSize="xs">
                {pendingCount} pending acknowledgment
              </Badge>
            )}
            <Flex gap={2} ml="auto" align="center">
              {!isLogging ? (
                <SolidActionButton size="xs" fontFamily="Hero-New" onClick={openLog}>
                  + Log Lab Result
                </SolidActionButton>
              ) : null}
              <CloseButton onClick={isLogging ? closeLog : onClose} />
            </Flex>
          </Flex>
        </ModalHeader>

        {isLogging ? (
          <ModalBody p={0} flex={1} overflowY="auto">
            <LogLabResultForm
              assessmentId={assessmentId}
              patientId={patientId}
              onSuccess={closeLog}
              onCancel={closeLog}
            />
          </ModalBody>
        ) : (
          <>
            <Flex
              px={4}
              py={2}
              borderBottom="1px solid"
              borderColor="gray.100"
              bg="gray.50"
              align="center"
              gap={2}
              flexShrink={0}>
              <Text fontSize="xs" color="gray.500" fontFamily="Hero-New" fontWeight={600}>
                Sort:
              </Text>
              <ButtonGroup size="xs" isAttached variant="outline">
                {(Object.keys(sortLabel) as SortMode[]).map((mode, i) => (
                  <Button
                    key={mode}
                    colorScheme={sortMode === mode ? 'sage' : 'gray'}
                    fontWeight={500}
                    bg={sortMode === mode ? 'sage.50' : undefined}
                    fontFamily="Hero-New"
                    borderLeftWidth={i === 0 || sortMode === mode ? 1 : 0}
                    // borderRightWidth={sortMode === mode ? 1 : 0}
                    onClick={() => setSortMode(mode)}>
                    {sortLabel[mode]}
                  </Button>
                ))}
              </ButtonGroup>
            </Flex>

            <ModalBody flex={1} overflowY="auto" py={3} px={4}>
              {loading ? (
                <Flex justify="center" align="center" py={12}>
                  <Spinner color="blue.400" />
                </Flex>
              ) : sorted.length === 0 ? (
                <Box py={10} textAlign="center">
                  <Text color="gray.400" fontFamily="Hero-New" fontSize="sm">
                    No lab results recorded yet.
                  </Text>
                  <SolidActionButton mt={3} size="sm" fontFamily="Hero-New" onClick={openLog}>
                    + Log First Result
                  </SolidActionButton>
                </Box>
              ) : (
                <Flex direction="column" gap={2}>
                  {sorted.map(r => (
                    <LabResultRow key={r.id} result={r} onSave={handleSave} />
                  ))}
                </Flex>
              )}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
